import { ActionIcon, Box, Flex, Group, Image, Modal, ScrollArea, Text, Tooltip } from "@mantine/core"
import { CaretLeftIcon, CaretRightIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon, XIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import noimage from "../../assets/product-placeholder.svg"
import classes from "./product-gallery.module.css"

export type GalleryImage = {
  id: string
  url: string
  isMain?: boolean
}

const ZOOM_MIN = 1
const ZOOM_MAX = 3
const ZOOM_STEP = 0.5

type Props = {
  images: GalleryImage[]
  /** Шинэ бараа руу шилжихэд эхний зураг руу буцаана */
  resetKey?: string
}

/**
 * Барааны зургийн галерей: гол зураг нь хүрээгээ бүтэн дүүргэнэ, хоёр тийш нь
 * сумаар гүйлгэнэ, дарвал бүтэн дэлгэцээр томорч, томорсон үедээ гүйлгэж
 * харах боломжтой.
 */
export function ProductGallery({ images, resetKey }: Props) {
  const { t } = useTranslation()
  const [index, setIndex] = React.useState<number>(0)
  const [opened, setOpened] = React.useState<boolean>(false)
  const [zoom, setZoom] = React.useState<number>(ZOOM_MIN)

  const count = images.length
  const hasMany = count > 1

  React.useEffect(() => {
    setIndex(0)
  }, [resetKey])

  // Зургийн тоо багасвал сонголт хүрээнээсээ гарахгүй байх ёстой
  React.useEffect(() => {
    setIndex((current) => (count === 0 ? 0 : Math.min(current, count - 1)))
  }, [count])

  const go = React.useCallback(
    (step: number) => {
      if (count === 0) return
      setIndex((current) => (current + step + count) % count)
      setZoom(ZOOM_MIN)
    },
    [count],
  )

  const select = (next: number) => {
    setIndex(next)
    setZoom(ZOOM_MIN)
  }

  // Томорсон харагдацад сумны товчлуураар гүйлгэнэ. Esc-ийг Modal өөрөө барина.
  React.useEffect(() => {
    if (!opened) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") go(-1)
      if (event.key === "ArrowRight") go(1)
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [opened, go])

  const current = images[index]
  const zoomLayerVars = { "--gallery-zoom": `${zoom}` } as React.CSSProperties

  return (
    <Box>
      <Box className={classes.stage}>
        <Image
          src={current?.url || noimage}
          fit="contain"
          w="100%"
          h="100%"
          fallbackSrc={noimage}
          className={classes.stageImage}
          onClick={() => setOpened(true)}
        />

        {hasMany && (
          <>
            <ActionIcon
              variant="default"
              radius="xl"
              size="lg"
              className={classes.navPrev}
              aria-label={t("order:buyerSales.imagePrev")}
              onClick={() => go(-1)}
            >
              <CaretLeftIcon size={18} />
            </ActionIcon>
            <ActionIcon
              variant="default"
              radius="xl"
              size="lg"
              className={classes.navNext}
              aria-label={t("order:buyerSales.imageNext")}
              onClick={() => go(1)}
            >
              <CaretRightIcon size={18} />
            </ActionIcon>
            <Box className={classes.counter}>
              <Text size="sm" fw={600}>
                {index + 1} / {count}
              </Text>
            </Box>
          </>
        )}
      </Box>

      {hasMany && (
        <Box mt="md">
          <ThumbnailStrip images={images} index={index} onSelect={select} />
        </Box>
      )}

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        fullScreen
        withCloseButton={false}
        padding={0}
        transitionProps={{ duration: 150 }}
        classNames={{ content: classes.lightboxContent, body: classes.lightboxBody }}
      >
        <Flex direction="column" className={classes.lightbox}>
          <Group justify="space-between" className={classes.lightboxBar}>
            <Text size="sm" fw={600} c="var(--mantine-color-gray-3)">
              {hasMany ? `${index + 1} / ${count}` : t("order:buyerSales.imageViewerTitle")}
            </Text>
            <Group gap="xs">
              <Tooltip label={t("order:buyerSales.imageZoomOut")} withArrow>
                <ActionIcon
                  variant="default"
                  radius="xl"
                  size="lg"
                  className={classes.lightboxButton}
                  disabled={zoom <= ZOOM_MIN}
                  aria-label={t("order:buyerSales.imageZoomOut")}
                  onClick={() => setZoom((value) => Math.max(ZOOM_MIN, value - ZOOM_STEP))}
                >
                  <MagnifyingGlassMinusIcon size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={t("order:buyerSales.imageZoomIn")} withArrow>
                <ActionIcon
                  variant="default"
                  radius="xl"
                  size="lg"
                  className={classes.lightboxButton}
                  disabled={zoom >= ZOOM_MAX}
                  aria-label={t("order:buyerSales.imageZoomIn")}
                  onClick={() => setZoom((value) => Math.min(ZOOM_MAX, value + ZOOM_STEP))}
                >
                  <MagnifyingGlassPlusIcon size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={t("order:buyerSales.imageClose")} withArrow>
                <ActionIcon
                  variant="default"
                  radius="xl"
                  size="lg"
                  className={classes.lightboxButton}
                  aria-label={t("order:buyerSales.imageClose")}
                  onClick={() => setOpened(false)}
                >
                  <XIcon size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          <Box className={classes.lightboxStage}>
            {hasMany && (
              <ActionIcon
                variant="default"
                radius="xl"
                size="xl"
                className={classes.lightboxPrev}
                aria-label={t("order:buyerSales.imagePrev")}
                onClick={() => go(-1)}
              >
                <CaretLeftIcon size={22} />
              </ActionIcon>
            )}

            {/* Томруулсан үед энэ хэсэг гүйлгэгддэг болно */}
            <Box className={classes.lightboxScroll} data-zoomed={zoom > ZOOM_MIN || undefined}>
              <Box className={classes.lightboxZoomLayer} style={zoomLayerVars}>
                <Image src={current?.url || noimage} fit="contain" w="100%" h="100%" fallbackSrc={noimage} />
              </Box>
            </Box>

            {hasMany && (
              <ActionIcon
                variant="default"
                radius="xl"
                size="xl"
                className={classes.lightboxNext}
                aria-label={t("order:buyerSales.imageNext")}
                onClick={() => go(1)}
              >
                <CaretRightIcon size={22} />
              </ActionIcon>
            )}
          </Box>

          {hasMany && (
            <Box className={classes.lightboxFoot}>
              <ThumbnailStrip images={images} index={index} onSelect={select} variant="lightbox" />
            </Box>
          )}
        </Flex>
      </Modal>
    </Box>
  )
}

/**
 * Жижиг зургийн эгнээ. Багтахгүй болмогц хоёр талдаа гүйлгэх товчтой болно —
 * эс тэгвээс зургууд таслагдаад, цаана нь өөр зураг байгаа нь мэдэгдэхгүй.
 */
function ThumbnailStrip({
  images,
  index,
  onSelect,
  variant = "stage",
}: {
  images: GalleryImage[]
  index: number
  onSelect: (next: number) => void
  variant?: "stage" | "lightbox"
}) {
  const { t } = useTranslation()
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const [edges, setEdges] = React.useState<{ left: boolean; right: boolean }>({ left: false, right: false })

  const measure = React.useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    setEdges({
      left: viewport.scrollLeft > 2,
      right: viewport.scrollLeft + viewport.clientWidth < viewport.scrollWidth - 2,
    })
  }, [])

  React.useEffect(() => {
    measure()
    const viewport = viewportRef.current
    if (!viewport) return

    viewport.addEventListener("scroll", measure, { passive: true })
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure)
    observer?.observe(viewport)

    return () => {
      viewport.removeEventListener("scroll", measure)
      observer?.disconnect()
    }
  }, [measure, images.length])

  // Сумаар зураг солиход идэвхтэй жижиг зураг харагдацад орж ирнэ.
  // behavior нь "auto" — "smooth" бүх орчинд найдвартай ажиллахгүй.
  React.useEffect(() => {
    const viewport = viewportRef.current
    const active = viewport?.querySelector<HTMLElement>("[data-active]")
    active?.scrollIntoView({ behavior: "auto", block: "nearest", inline: "nearest" })
    measure()
  }, [index, measure])

  const scrollByPage = (direction: number) => {
    const viewport = viewportRef.current
    if (!viewport) return
    viewport.scrollBy({ left: direction * viewport.clientWidth * 0.8, behavior: "auto" })
    // scroll эвент бүх орчинд найдвартай гарахгүй тул шууд дахин хэмжинэ
    measure()
  }

  return (
    <Box className={classes.strip}>
      {edges.left && (
        <ActionIcon
          variant="default"
          radius="xl"
          size="sm"
          className={classes.stripPrev}
          aria-label={t("order:buyerSales.imagePrev")}
          onClick={() => scrollByPage(-1)}
        >
          <CaretLeftIcon size={13} />
        </ActionIcon>
      )}

      <ScrollArea type="auto" viewportRef={viewportRef} onScrollPositionChange={measure} scrollbarSize={6}>
        <Flex gap={10} className={variant === "lightbox" ? classes.lightboxThumbs : undefined}>
          {images.map((image, position) => (
            <Box
              key={image.id}
              className={classes.thumbnail}
              data-active={position === index || undefined}
              data-variant={variant}
              onClick={() => onSelect(position)}
            >
              <Image src={image.url} w="100%" h="100%" fit="contain" fallbackSrc={noimage} />
            </Box>
          ))}
        </Flex>
      </ScrollArea>

      {edges.right && (
        <ActionIcon
          variant="default"
          radius="xl"
          size="sm"
          className={classes.stripNext}
          aria-label={t("order:buyerSales.imageNext")}
          onClick={() => scrollByPage(1)}
        >
          <CaretRightIcon size={13} />
        </ActionIcon>
      )}
    </Box>
  )
}
